import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Shield, Clock, Users, ArrowRight, Star, Award, TrendingUp, Scale, Banknote, Timer, Euro } from "lucide-react";
import heroImage from "@/assets/hero-assurance-do.jpg";
import mascotBeaver from "@/assets/mascot-beaver-optimized.webp";
import ContactForm from "@/components/ContactForm";
import { ContextualLinks, CrossSellCTA } from "@/components/InternalLinking";
import { SEOHead } from "@/components/SEO/SEOProvider";
import { useAutoSitemapUpdate } from "@/hooks/useSitemapGeneration";
const Home = () => {
  useAutoSitemapUpdate();
  const benefits = [{
    icon: Scale,
    title: "Obligatoire par la loi",
    description: "L'assurance DO est obligatoire pour tous les travaux de construction supérieurs à 1600€ TTC."
  }, {
    icon: Banknote,
    title: "Remboursement rapide",
    description: "En cas de sinistre, obtenez une indemnisation sans attendre les procédures judiciaires."
  }, {
    icon: Timer,
    title: "Gain de temps",
    description: "Évitez les longs litiges et bénéficiez d'une prise en charge immédiate des dommages."
  }];
  const popularGuides = [{
    title: "Assurance DO : obligatoire ou pas ?",
    description: "Tout comprendre sur l'obligation légale de l'assurance Dommages-Ouvrage",
    slug: "assurance-do-obligatoire",
    readTime: "5 min"
  }, {
    title: "Prix d'une DO en 2025 : comment est calculé le coût ?",
    description: "Découvrez les facteurs qui influencent le prix de votre assurance DO",
    slug: "prix-assurance-do-2025",
    readTime: "7 min"
  }, {
    title: "DO vs garantie décennale : les vraies différences",
    description: "Ne confondez plus ces deux assurances construction essentielles",
    slug: "dommages-ouvrage-vs-garantie-decennale",
    readTime: "8 min"
  }, {
    title: "Comment souscrire une DO étape par étape",
    description: "Guide complet pour souscrire votre assurance Dommages-Ouvrage facilement",
    slug: "comment-souscrire-assurance-do",
    readTime: "12 min"
  }, {
    title: "Déclarer un sinistre DO : délais et procédure",
    description: "Procédure complète pour déclarer et obtenir l'indemnisation de votre sinistre",
    slug: "declarer-sinistre-do",
    readTime: "7 min"
  }, {
    title: "Assurance DO pour maisons individuelles",
    description: "Spécificités de l'assurance DO pour la construction de maisons individuelles",
    slug: "assurance-do-maisons-individuelles",
    readTime: "5 min"
  }];
  return <div className="flex-1">
      <SEOHead 
        title="Assurance Dommages-Ouvrage | Guide complet et devis DO"
        description="Guide complet sur l'assurance Dommages-Ouvrage : obligation légale, tarifs, procédures. Obtenez votre devis DO personnalisé gratuitement."
        canonical="https://assurancedo.fr/"
        keywords={["assurance dommages-ouvrage", "DO", "construction", "maître d'ouvrage", "garantie décennale", "devis assurance DO"]}
        ogImage="https://assurancedo.fr/hero-assurance-do.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "AssuranceDO.fr",
          "description": "Guide complet sur l'assurance dommages-ouvrage",
          "url": "https://assurancedo.fr/",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://assurancedo.fr/guides?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-hero text-primary-foreground">
        <div className="absolute inset-0 bg-black/20 pointer-events-none" />
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="flex flex-col xl:flex-row items-center justify-center gap-8 xl:gap-12">
            {/* Left: Mascot + Content */}
            <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-4 lg:gap-8 flex-1">
              {/* Mascot Column */}
              <div className="flex-shrink-0">
                <div className="animate-fade-in">
                  <img 
                    src={mascotBeaver}
                    alt="Mascotte castor expert en assurance DO"
                    className="w-48 h-48 will-change-transform transition-transform duration-300 hover:scale-105"
                    width="192"
                    height="192"
                    loading="eager"
                    fetchPriority="high"
                  />
                </div>
              </div>
              
              {/* Content Column */}
              <div className="text-center lg:text-left max-w-2xl">
                <h1 className="hero-title text-4xl md:text-6xl font-bold mb-6 drop-shadow-lg">
                  Assurance Dommages-Ouvrage
                </h1>
                <p className="text-xl md:text-2xl mb-4 opacity-90 drop-shadow">Tout comprendre, comparer et être accompagné !</p>
                <p className="text-lg mb-8 opacity-80 drop-shadow">
                  Guides, conseils et accompagnement personnalisé pour réussir votre projet de construction
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Button asChild size="xl" variant="hero">
                    <Link to="/contact">
                      Parler de mon projet
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button asChild size="xl" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                    <Link to="/guides">
                      Consulter nos guides
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            {/* Right: Contact Form - Desktop only */}
            <div className="hidden xl:block flex-shrink-0 w-full max-w-md xl:max-w-sm">
              <ContactForm variant="compact" title="Parlez-nous de votre projet" description="Recevez des conseils personnalisés" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 lg:py-24 bg-surface">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Pourquoi l'assurance DO est essentielle ?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              L'assurance Dommages-Ouvrage vous protège efficacement lors de vos travaux de construction ou de rénovation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return <Card key={index} className="text-center shadow-sm hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </section>

      {/* Popular Guides Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Nos guides populaires
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Découvrez nos articles les plus consultés pour tout comprendre sur l'assurance Dommages-Ouvrage
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularGuides.map((guide, index) => <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">{guide.readTime} de lecture</span>
                    <CheckCircle className="h-4 w-4 text-success" />
                  </div>
                  <CardTitle className="text-lg leading-tight">
                    <Link to={`/guides/${guide.slug}`} className="hover:text-primary transition-colors">
                      {guide.title}
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4">
                    {guide.description}
                  </CardDescription>
                  <Button asChild variant="ghost" size="sm" className="p-0 h-auto font-medium">
                    <Link to={`/guides/${guide.slug}`}>
                      Lire l'article
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>)}
          </div>

          <div className="text-center mt-12">
            <Button asChild variant="cta" size="lg">
              <Link to="/guides">
                Voir tous nos guides
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Maillage interne contextuel */}
      <section className="py-12 bg-surface">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ContextualLinks pageType="home" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Prêt à parler de votre projet ?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Recevez rapidement des informations personnalisées pour votre assurance Dommages-Ouvrage. 
            Nos experts vous accompagnent dans votre projet.
          </p>
          <Button asChild size="xl" variant="hero" className="bg-white text-primary hover:bg-white/90">
            <Link to="/contact">
              Être mis en relation avec un expert
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Section liens internes enrichis */}
      <section className="py-16 bg-muted/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Ressources essentielles</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Accédez rapidement aux informations les plus importantes sur l'assurance Dommages-Ouvrage
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Scale className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">
                  <Link to="/guides/assurance-do-obligatoire" className="hover:text-primary transition-colors">
                    Obligation légale
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="mb-4">
                  Découvrez dans quels cas l'assurance DO est obligatoire par la loi française
                </CardDescription>
                <Button asChild variant="outline" size="sm">
                  <Link to="/guides/assurance-do-obligatoire">
                    Tout comprendre
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Euro className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">
                  <Link to="/guides/prix-assurance-do-2025" className="hover:text-primary transition-colors">
                    Prix et calcul
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="mb-4">
                  Calculez le coût de votre assurance DO avec nos barèmes 2025 actualisés
                </CardDescription>
                <Button asChild variant="outline" size="sm">
                  <Link to="/guides/prix-assurance-do-2025">
                    Calculer le prix
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">
                  <Link to="/guides/comment-souscrire-assurance-do" className="hover:text-primary transition-colors">
                    Comment souscrire
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="mb-4">
                  Guide étape par étape pour souscrire votre assurance DO sans erreur
                </CardDescription>
                <Button asChild variant="outline" size="sm">
                  <Link to="/guides/comment-souscrire-assurance-do">
                    Suivre le guide
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">
                  <Link to="/guides/que-couvre-assurance-do" className="hover:text-primary transition-colors">
                    Couverture
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="mb-4">
                  Découvrez exactement ce qui est couvert par l'assurance Dommages-Ouvrage
                </CardDescription>
                <Button asChild variant="outline" size="sm">
                  <Link to="/guides/que-couvre-assurance-do">
                    Voir la couverture
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>;
};
export default Home;